import AdvancedSearch from '@app/components/AdvancedSearch'
import BidirectionalSort from '@app/components/BidirectionalSort'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import PropertyTypeSelector from '@app/components/PropertyTypeSelector'
import SearchBar from '@app/components/SearchBar'
import { Attachment } from '@app/models/Attachment'
import { ImageSize, PropertyType } from '@app/models/enums'
import { IProperty } from '@app/models/Property'
import { IGlobalConfigurationState } from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { formatAddressSingleLine, humanize } from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { List } from 'immutable'
import * as qs from 'query-string'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link, RouteComponentProps } from 'react-router-dom'
import { Column } from 'react-virtualized'
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap'
import { actionCreators, IPropertiesListState } from '../modules/propertiesList'

type PropertiesListProps = IPropertiesListState &
  IGlobalConfigurationState &
  typeof actionCreators &
  RouteComponentProps<{}>

export default class ConnectionsListPage extends React.PureComponent<
  PropertiesListProps
> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: PropertiesListProps) {
    super(props)
    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public componentDidMount() {
    if (this.props.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }
  }

  public componentDidUpdate(prevProps: PropertiesListProps) {
    if (this.props.location.search !== prevProps.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }
  }

  public render() {
    return (
      <DocumentTitle title='Properties'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <div className='mb-2 d-flex flex-row justify-content-between'>
              <h3>Properties</h3>
              <Link to='property/add' className='align-self-center'>
                <Button size='sm' color='primary'>
                  <FontAwesomeIcon icon={['fal', 'plus']} fixedWidth />
                  <span>Add Property</span>
                </Button>
              </Link>
            </div>
            <SearchBar
              onChange={this._onChangeFilter}
              placeholder='Looking for something?'
              size='sm'
              value={this.props.filterText}
            />
            <AdvancedSearch className='mt-1' expand='md'>
              <Col md={6} xl={6}>
                <FormGroup>
                  <Label>Property Type</Label>
                  <PropertyTypeSelector
                    value={this.props.propertyTypes}
                    placeholder={
                      <div color='black'>Select property types...</div>
                    }
                    includeAllOption={true}
                    bsSize='sm'
                    onChange={this._onChangePropertyType}
                  />
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Sort By</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.sortOrder}
                    onChange={this._onChangeSortOrder}
                  >
                    {this.props.configuration.propertySortOptions
                      .map((p, i) => (
                        <option key={`sort-${i}`} value={p}>
                          {humanize(p)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <BidirectionalSort
                md={4}
                xl={3}
                orderBy={this.props.orderBy}
                changeOrdering={this._onChangeOrderBy}
              />
            </AdvancedSearch>
          </div>
          <div>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              loadItems={this.props.loadProperties}
              noRowsRenderer={this._noRowsRenderer}
              onRowClicked={this._onRowClicked}
              totalItems={this.props.totalItems}
              ref={this.tableRef}
              rowHeight={65}
            >
              <Column
                cellRenderer={({ cellData }) => {
                  const imgUrl = cellData
                    ? (cellData as Attachment).getImageUrl(ImageSize.h50w50)
                    : '/images/default-property_h_50_w_50.png'
                  return (
                    <img
                      src={imgUrl}
                      height='40'
                      width='40'
                      style={{ borderRadius: '5px' }}
                    />
                  )
                }}
                dataKey='thumbnailImage'
                flexGrow={0}
                flexShrink={0}
                width={40}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const property = rowData as IProperty
                  return (
                    <div>
                      <div className='font-weight-bold text-truncate'>
                        {property.name}
                      </div>
                      <div className='small text-truncate'>
                        {formatAddressSingleLine(property.address)}
                      </div>
                    </div>
                  )
                }}
                dataKey='information'
                flexGrow={1}
                label='Property'
                width={300}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const contact = rowData as IProperty
                  return contact.owners.size > 0
                    ? contact.owners
                        .map((o) => (o !== undefined ? o.name : ''))
                        .join(', ')
                    : ''
                }}
                className='d-none d-md-block'
                dataKey='owners'
                flexGrow={1}
                headerClassName='d-none d-md-block'
                label='Owners'
                width={100}
                maxWidth={300}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private _noRowsRenderer() {
    return <div>No properties</div>
  }

  private _onRowClicked(item: IProperty, index: number) {
    window.open(
      `${window.location.pathname.replace(
        'properties/properties',
        `properties/property/${item.id}`,
      )}`,
    )
  }

  private _onChangePropertyType(value: List<PropertyType>) {
    this.props.setPropertyTypes(value)
    this._reloadData()
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _onChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSortOrder(event.target.value)
    this._reloadData()
  }

  private _onChangeOrderBy(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setOrderBy(event.target.value)
    this._reloadData()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}
